// src/components/modules/Dashboard.js
// "My Dashboard" — personal timesheet view (MTD / missed days / YTD) + org overview.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { useMsal } from "@azure/msal-react";
import { FiAlertTriangle, FiPlus, FiChevronRight, FiChevronDown, FiChevronLeft, FiSearch, FiUsers, FiShield, FiUser, FiGlobe, FiX, FiLayers, FiDownload } from "react-icons/fi";
import { buildDashboardStats, toISO, HOURS_PER_DAY } from "./dashboardStats";
import { fetchMyEntries, fetchHolidays, fetchOrgCounts, fetchOrganizationSummary, NotConfiguredError } from "./dashboardApi";
import { fetchActiveClients, fetchEmployeeUtilization, periodRange, periodLabel, isFps } from "./Utilizationapi";
import { RocketlaneSyncLog } from "./Rocketlanesynclog";

// Organisation view client filter
const GROUP_LABEL = { all: "All employees", freshworks: "Freshworks (Rocketlane)", legacy: "Legacy", unlisted: "Not on HR list (Rocketlane)" };

// Roles that get the "Admin view" (Clients + Organization overview). 3 = ADMIN.
const ADMIN_ROLE_IDS = ["3"];

// Id of an empty element in Header.js, next to "Hi, <name>". Dashboard buttons render there when it exists.
const HEADER_SLOT_ID = "header-actions";

// Design tokens (match the Figma/Design artifact)
const C = {
  primary: "#1D5FD1",
  nonBill: "#9DB8E8",
  track: "#F1F3F7",
  warn: "#C2410C",
  warnSoft: "#FDE6D6",
  warnBorder: "#F6C9A8",
  holiday: "#7C3AED",     // holiday hours / General holiday
  holidaySoft: "#EDE9FE",
};

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 1 });
const longDate = (d) => d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const shortDate = (d) => d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
const isoDate = (iso) => new Date(`${iso}T00:00:00`);
// "Worked on Vijaya Dashami" / "Gandhi Jayanti, Vijaya Dashami · not worked" / "No holidays this month"
const holidaySub = (list = [], org) => {
  if (!list.length) return "No holidays this month";
  const general = list.filter((h) => h.type === "General");
  const when = (general.length ? general : list)
    .map((h) => isoDate(h.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })).join(", ");
  const worked = list.filter((h) => h.hours > 0);
  if (!org && worked.length) return `Worked on ${worked.map((h) => h.name).join(", ")} · view days`;
  return org ? `Holidays ${when} · view who worked` : `Holidays ${when} · not worked`;
};
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

// Calendar cell styles — full class strings so Tailwind JIT picks them up.
const CELL = {
  blank: "bg-transparent",
  off: "bg-[#F1F3F7] text-[#8A93A3]",
  logged: "bg-[#E3ECFB] text-[#164AA6]",
  missed: "bg-[#FDE6D6] text-[#9A3412] font-bold border-[1.5px] border-[#EA7A3A]",
  today: "bg-white text-[#0F172A] font-bold border-2 border-[#1D5FD1]",
  future: "bg-white text-[#8A93A3] border border-dashed border-[#D5DAE3]",
  holiday: "bg-[#DDD6FE] text-[#5B21B6] font-semibold",
  restricted: "bg-[#F5F3FF] text-[#6D28D9] border border-dashed border-[#A78BFA]",
  leave: "bg-[#ECFDF5] text-[#047857]",
};
const CELL_LABEL = {
  off: "Weekend", logged: "Logged", missed: "Missed", today: "Today", future: "Upcoming",
  holiday: "Holiday", restricted: "Restricted holiday (optional)", leave: "Leave",
};

// ── Small building blocks ────────────────────────────────────────────
const Card = ({ as: Tag = "div", className = "", children, ...rest }) => (
  <Tag className={`bg-white border border-[#E6E9EF] rounded-[14px] ${className}`} {...rest}>{children}</Tag>
);

const Swatch = ({ color, round }) => (
  <span aria-hidden="true" className={`inline-block w-[10px] h-[10px] shrink-0 ${round ? "rounded-full" : "rounded-[3px]"}`} style={{ background: color }} />
);

const Kpi = ({ label, swatch, value, unit, sub, warn, onClick }) => (
  <Card {...(onClick ? { as: "button", type: "button", onClick, title: "Show the days" } : {})}
    className={`p-5 flex flex-col gap-1.5 text-left ${warn ? "!border-[#F6C9A8]" : ""} ${onClick ? "cursor-pointer hover:!border-[#A9C1EC] hover:shadow-sm focus-visible:outline-2 focus-visible:outline-[#1D5FD1]" : ""}`}>
    <span className={`text-[13px] flex items-center gap-2 ${warn ? "text-[#9A3412]" : "text-[#5B6474]"}`}>
      {swatch && <Swatch color={swatch} round={warn} />}{label}
    </span>
    <span className={`text-[32px] font-semibold leading-tight ${warn ? "text-[#C2410C]" : "text-[#0F172A]"}`}>
      {value}
      {unit && <span className={`text-[16px] font-medium ${warn ? "text-[#9A3412]" : "text-[#5B6474]"}`}> {unit}</span>}
    </span>
    {sub && <span className={`text-[12px] ${warn ? "text-[#7C2D12]" : "text-[#5B6474]"}`}>{sub}</span>}
  </Card>
);

// A figure that opens the day-by-day list (Extra / Holiday hours)
const DetailFigure = ({ label, title, value, swatch, big, onClick }) => (
  <button type="button" onClick={onClick} title={`${title} — click to see the days`}
    className={`flex flex-col ${big ? "gap-0.5" : "gap-1"} text-left bg-transparent border-0 p-0 cursor-pointer group`}>
    <span className="text-[12px] text-[#5B6474] flex items-center gap-1 whitespace-nowrap">
      {swatch && <Swatch color={swatch} />}{label}
      <FiChevronRight aria-hidden="true" className="text-[#8A93A3] group-hover:text-[#1D5FD1]" />
    </span>
    <span className={`${big ? "text-[22px]" : "text-[18px]"} font-semibold text-[#0F172A] underline decoration-dotted decoration-[#A9B3C4] underline-offset-4 group-hover:decoration-[#1D5FD1]`}>
      {value}
    </span>
  </button>
);

const REASON = { weekend: "Weekend", leave: "Leave day", above_8h: "Above 8 h" };

// Day-by-day list behind Extra hours / Holiday hours. Organisation view adds the employee column.
const HoursDetailModal = ({ type, scope, setScope, rows, showEmployee, monthName, onClose }) => {
  const [query, setQuery] = useState("");
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const isExtra = type === "extra";
  const q = query.trim().toLowerCase();
  const list = rows.filter((r) => !q || (r.employeeName || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q));
  const total = list.reduce((a, r) => a + (isExtra ? r.extra : r.hours), 0);
  const people = new Set(list.map((r) => r.employeeId)).size;
  const dayFmt = (d) => d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  const th = "py-2 px-3 font-medium text-[12px] text-[#5B6474] text-left whitespace-nowrap";
  const td = "py-2 px-3 text-[13px] whitespace-nowrap";
  const tab = (on) => `h-8 px-3 rounded-lg border-0 text-[12px] cursor-pointer ${on ? "bg-white text-[#164AA6] font-semibold shadow-sm" : "bg-transparent text-[#3D4555]"}`;

  return createPortal(
    <div role="presentation" onClick={onClose}
      className="fixed inset-0 z-[1000] bg-[rgba(15,23,42,0.45)] flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div role="dialog" aria-modal="true" aria-labelledby="hours-detail-title" onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[14px] w-full max-w-[760px] max-h-[86vh] flex flex-col shadow-xl text-[#0F172A]">
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-[#EEF0F4]">
          <div className="flex flex-col gap-1">
            <h2 id="hours-detail-title" className="m-0 text-[18px] font-semibold flex items-center gap-2">
              <Swatch color={isExtra ? "#0F172A" : C.holiday} />{isExtra ? "Extra hours" : "Holiday hours"}
            </h2>
            <span className="text-[13px] text-[#5B6474]">
              {isExtra
                ? "Saturday / Sunday and leave-day hours, plus hours above 8 h on a working day"
                : "Hours logged on company holidays (not counted as extra)"}
            </span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="w-9 h-9 rounded-lg border-0 bg-transparent text-[#5B6474] cursor-pointer hover:bg-[#F4F6FA] flex items-center justify-center text-[18px]">
            <FiX aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
          <div role="tablist" aria-label="Period" className="flex bg-[#F1F3F7] rounded-[10px] p-1 gap-1">
            <button type="button" role="tab" aria-selected={scope === "month"} className={tab(scope === "month")} onClick={() => setScope("month")}>{monthName}</button>
            <button type="button" role="tab" aria-selected={scope === "year"} className={tab(scope === "year")} onClick={() => setScope("year")}>Year to date</button>
          </div>
          {showEmployee && (
            <label className="relative">
              <FiSearch aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93A3]" />
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search employee"
                aria-label="Search employee" className="h-9 pl-9 pr-3 rounded-[10px] border border-[#D5DAE3] text-[13px] w-[220px]" />
            </label>
          )}
        </div>

        <div className="px-5 pb-2 text-[13px] text-[#3D4555]">
          <strong className="text-[#0F172A]">{fmt(total)} h</strong> on {list.length} day{list.length === 1 ? "" : "s"}
          {showEmployee ? ` · ${people} employee${people === 1 ? "" : "s"}` : ""}
        </div>

        <div className="overflow-auto px-5 pb-5">
          {list.length === 0 ? (
            <p className="m-0 py-6 text-center text-[13px] text-[#5B6474]">
              {isExtra ? "No extra hours" : "No hours on holidays"} {scope === "month" ? `in ${monthName}` : "this year"}.
            </p>
          ) : (
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-[#EEF0F4]">
                  <th className={th}>Date</th>
                  {showEmployee && <th className={th}>Employee</th>}
                  {isExtra ? (<>
                    <th className={`${th} text-right`}>Logged</th>
                    <th className={`${th} text-right`}>Regular</th>
                    <th className={`${th} text-right`}>Extra</th>
                    <th className={th}>Why</th>
                  </>) : (<>
                    <th className={th}>Holiday</th>
                    <th className={`${th} text-right`}>Hours</th>
                  </>)}
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={`${r.iso}-${r.employeeId ?? "me"}`} className="border-b border-[#F1F3F7] last:border-0">
                    <td className={td}>{dayFmt(r.date)}</td>
                    {showEmployee && <td className={`${td} whitespace-normal`}>{r.employeeName}</td>}
                    {isExtra ? (<>
                      <td className={`${td} text-right tabular-nums`}>{fmt(r.logged)} h</td>
                      <td className={`${td} text-right tabular-nums text-[#5B6474]`}>{fmt(r.regular)} h</td>
                      <td className={`${td} text-right tabular-nums font-semibold`}>{fmt(r.extra)} h</td>
                      <td className={td}>
                        <span className={`inline-flex h-6 items-center px-2 rounded-full text-[11px] font-semibold ${r.reason === "above_8h" ? "bg-[#E3ECFB] text-[#164AA6]" : r.reason === "leave" ? "bg-[#ECFDF5] text-[#047857]" : "bg-[#F1F3F7] text-[#3D4555]"}`}>
                          {REASON[r.reason] || r.reason}
                        </span>
                      </td>
                    </>) : (<>
                      <td className={`${td} whitespace-normal`}>
                        {r.name}{r.type === "Restricted" && <span className="ml-1.5 text-[11px] text-[#6D28D9]">(restricted)</span>}
                      </td>
                      <td className={`${td} text-right tabular-nums font-semibold`}>{fmt(r.hours)} h</td>
                    </>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Admin view → Employees: each person's hours, split by client → project
const TeamBadge = ({ team }) => (
  <span className={`inline-flex items-center h-5 px-1.5 rounded-full text-[10px] font-semibold ${
    team === "Freshworks" ? "bg-[#E6F4F1] text-[#1E6B52]" : team === "Not on HR list" ? "bg-[#FFF4EC] text-[#9A3412]" : "bg-[#F1F3F7] text-[#3D4555]"}`}>
    {team === "Freshworks" ? "Rocketlane" : team === "Not on HR list" ? "Not on HR list" : "Legacy"}
  </span>
);

const FPS_COLOR = "#7C3AED";   // FPS projects (always with the "FPS" label too)
const FpsChip = () => (
  <span className="inline-flex items-center h-[18px] px-1.5 rounded-full text-[10px] font-semibold bg-[#F1ECFB] text-[#5B33A6] shrink-0">FPS</span>
);
// Hours on FPS projects (name contains "FPS") for one employee row
const empFpsHours = (e) => (e.clients || []).reduce((t, c) => t + (c.projects || []).filter((p) => isFps(p.project_name))
  .reduce((u, p) => u + (Number(p.hours) || 0), 0), 0);

// query: the search box in the Admin view toolbar
const EmployeesPanel = ({ data, state, periodLabel, maxRows = 12, query = "" }) => {
  const [open, setOpen] = useState(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState("hours"); // hours | utilization | name

  if (state === "error") return <p role="alert" className="m-0 text-[13px] text-[#7C2D12]">Couldn't load employees. Try refreshing.</p>;
  if (state !== "ready") return (
    <div className="flex flex-col gap-2">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-12 rounded-lg bg-[#F1F3F7] animate-pulse" />)}</div>
  );

  const q = query.trim().toLowerCase();
  const sorted = [...(data?.employees || [])]
    .filter((e) => !q || (q === "fps" && empFpsHours(e) > 0) || (e.employee_name || "").toLowerCase().includes(q)
      || (e.clients || []).some((c) => (c.client_name || "").toLowerCase().includes(q)
        || (c.projects || []).some((p) => (p.project_name || "").toLowerCase().includes(q))))
    .sort((a, b) => sort === "name" ? (a.employee_name || "").localeCompare(b.employee_name || "")
      : sort === "utilization" ? b.utilization_pct - a.utilization_pct
      : sort === "fps" ? empFpsHours(b) - empFpsHours(a) : b.hours - a.hours);
  const anyFps = (data?.employees || []).some((e) => empFpsHours(e) > 0);
  const rows = showAll || q ? sorted : sorted.slice(0, maxRows);
  const toggle = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const thBase = "py-2 px-2 font-medium text-[12px] text-[#5B6474] whitespace-nowrap";
  const th = `${thBase} text-right`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[13px] text-[#5B6474]">
          {sorted.length} employee{sorted.length === 1 ? "" : "s"} · hours in {periodLabel} · click a row to see clients and projects
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort"
            className="h-10 px-3 rounded-[10px] border border-[#E6E9EF] bg-white text-[13px] cursor-pointer">
            <option value="hours">Most hours</option>
            <option value="utilization">Highest utilization</option>
            {anyFps && <option value="fps">Most FPS hours</option>}
            <option value="name">Name A–Z</option>
          </select>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="m-0 text-[13px] text-[#5B6474]">{q ? `No employee, client or project matches "${query}".` : "No employees in this group."}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-[#EEF0F4]">
                <th className={`${thBase} text-left pl-1`}>Employee</th>
                <th className={th}>Hours</th>
                <th className={th}>Billable</th>
                {anyFps && <th className={th} title="Hours on FPS projects (included in Hours)">FPS</th>}
                <th className={th}>Capacity</th>
                <th className={`${thBase} text-left w-[180px]`}>Utilization</th>
                <th className={`${th} pr-1`}>Clients</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const isOpen = open.has(e.employee_id);
                const over = e.utilization_pct > 100;
                return (
                  <React.Fragment key={e.employee_id}>
                    <tr onClick={() => toggle(e.employee_id)} aria-expanded={isOpen}
                      className="border-b border-[#F1F3F7] cursor-pointer hover:bg-[#F8FAFC]">
                      <td className="py-2.5 pl-1 pr-2">
                        <span className="flex items-center gap-2 min-w-0">
                          {isOpen ? <FiChevronDown aria-hidden="true" className="shrink-0 text-[#1D5FD1]" /> : <FiChevronRight aria-hidden="true" className="shrink-0 text-[#8A93A3]" />}
                          <span className="flex flex-col min-w-0">
                            <span className="text-[14px] font-medium truncate">{e.employee_name}</span>
                            <span className="flex items-center gap-1.5 text-[11px] text-[#5B6474]">
                              <TeamBadge team={e.team} />
                              {e.role && <span className="truncate">{e.role}</span>}
                              {!e.timesheet_account && e.team !== "Not on HR list" && <span className="text-[#6D28D9]">· no timesheet account</span>}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-right text-[14px] font-semibold tabular-nums">{fmt(e.hours)} h</td>
                      <td className="py-2.5 px-2 text-right text-[13px] tabular-nums text-[#3D4555]">{e.hours ? `${fmt(e.billable_pct)}%` : "—"}</td>
                      {anyFps && <td className="py-2.5 px-2 text-right text-[13px] tabular-nums text-[#5B33A6]">{empFpsHours(e) > 0 ? `${fmt(empFpsHours(e))} h` : "—"}</td>}
                      <td className="py-2.5 px-2 text-right text-[13px] tabular-nums text-[#5B6474]">{fmt(e.capacity_hours)} h</td>
                      <td className="py-2.5 px-2">
                        <span className="flex items-center gap-2">
                          <span className="flex-1 h-1.5 bg-[#F1F3F7] rounded-full overflow-hidden">
                            <span className="block h-1.5 rounded-full" style={{ width: `${Math.min(100, e.utilization_pct)}%`, background: over ? "#7C3AED" : C.primary }} />
                          </span>
                          <span className={`w-[52px] text-right text-[13px] tabular-nums font-semibold ${over ? "text-[#5B21B6]" : ""}`}>{fmt(e.utilization_pct)}%</span>
                        </span>
                      </td>
                      <td className="py-2.5 pl-2 pr-1 text-right text-[13px] tabular-nums">{e.clients_count}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-[#FAFBFC] border-b border-[#EEF0F4]">
                        <td colSpan={anyFps ? 7 : 6} className="px-4 py-3">
                          {(e.clients || []).length === 0 ? (
                            <span className="text-[13px] text-[#5B6474]">No hours logged in {periodLabel}.</span>
                          ) : (
                            <div className="flex flex-col gap-3">
                              {e.clients.map((c) => (
                                <div key={c.client_id} className="flex flex-col gap-1">
                                  <div className="flex items-baseline justify-between gap-3 border-b border-[#EEF0F4] pb-1">
                                    <span className="text-[13px] font-semibold">{c.client_name}</span>
                                    <span className="text-[13px] font-semibold tabular-nums">
                                      {fmt(c.hours)} h
                                      <span className="ml-2 text-[11px] font-normal text-[#5B6474]">{c.hours ? `${fmt((c.billable_hours / c.hours) * 100)}% billable` : ""}</span>
                                    </span>
                                  </div>
                                  {c.projects.map((p) => (
                                    <div key={p.project_id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 pl-3 text-[12px]">
                                      <span className="flex items-center gap-1.5 min-w-0 text-[#3D4555]" title={p.project_name}><span className="truncate">{p.project_name}</span>{isFps(p.project_name) && <FpsChip />}</span>
                                      <span className="tabular-nums text-[#3D4555]">
                                        {fmt(p.hours)} h
                                        {p.billable_hours < p.hours && <span className="ml-1.5 text-[#8A93A3]">({fmt(p.hours - p.billable_hours)} h non-billable)</span>}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!q && sorted.length > maxRows && (
        <button type="button" onClick={() => setShowAll((x) => !x)}
          className="self-start h-9 px-3 rounded-[10px] border-0 bg-transparent text-[13px] font-semibold text-[#1D5FD1] cursor-pointer hover:bg-[#EEF3FC]">
          {showAll ? "Show fewer" : `Show all ${sorted.length} employees`}
        </button>
      )}
    </div>
  );
};

const Legend = ({ color, label, value, warn }) => (
  <div className="flex flex-col gap-1">
    <span className={`text-[12px] flex items-center gap-1.5 ${warn ? "text-[#9A3412]" : "text-[#5B6474]"}`}>
      <Swatch color={color} />{label}
    </span>
    <span className={`text-[18px] font-semibold ${warn ? "text-[#C2410C]" : ""}`}>{value}</span>
  </div>
);

const OrgTile = ({ label, value, sub }) => (
  <div className="bg-white border border-[#E6E9EF] rounded-[12px] p-4 flex flex-col gap-0.5">
    <span className="text-[12px] text-[#5B6474]">{label}</span>
    <span className="text-[22px] font-semibold">{value}</span>
    {sub && <span className="text-[11px] text-[#5B6474]">{sub}</span>}
  </div>
);

// ── Component ────────────────────────────────────────────────────────
// Organisation view: who on the HR list isn't counted, and why — short, grouped, expandable.
//   needLogin: on the HR list today, no timesheet login and no Rocketlane time
//   notTracked: non-project staff (Employee_Master.Timesheet_Required = 0) — left out on purpose
const PeopleNote = ({ needLogin = [], notTracked = [] }) => {
  const [open, setOpen] = useState(false);
  const [showTracked, setShowTracked] = useState(false);
  if (needLogin.length === 0 && notTracked.length === 0) return null;
  const joined = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const shown = open ? needLogin : needLogin.slice(0, 4);
  return (
    <section aria-label="People not counted" className="rounded-[12px] border border-[#E6E9EF] bg-white px-4 py-3 flex flex-col gap-2.5 text-[13px]">
      {needLogin.length > 0 && (
        <>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-[#0F172A]">
              <strong className="font-semibold">{needLogin.length} {needLogin.length === 1 ? "person needs" : "people need"} a timesheet login</strong>
              <span className="text-[#5B6474]"> · on the HR list, but no login and no Rocketlane time yet, so not counted</span>
            </span>
            {needLogin.length > 4 && (
              <button type="button" onClick={() => setOpen((x) => !x)}
                className="border-0 bg-transparent p-0 text-[12px] font-semibold text-[#1D5FD1] cursor-pointer">
                {open ? "Show fewer" : `Show all ${needLogin.length}`}
              </button>
            )}
          </div>
          <ul className="m-0 p-0 list-none flex flex-wrap gap-2">
            {shown.map((x) => (
              <li key={x.emp_code} className="flex flex-wrap items-center gap-1.5 min-h-[32px] pl-3 pr-2.5 rounded-full bg-[#F4F6FA] border border-[#E6E9EF]">
                <span className="font-medium text-[#0F172A]">{x.employee_name}</span>
                {x.designation && <span className="text-[12px] text-[#5B6474]">· {x.designation}</span>}
                {x.date_of_joining && <span className="text-[12px] text-[#5B6474]">· joined {joined(x.date_of_joining)}</span>}
                {x.new_joiner && <span className="ml-0.5 px-1.5 rounded-full bg-[#E3ECFB] text-[#164AA6] text-[11px] font-semibold">New</span>}
              </li>
            ))}
          </ul>
        </>
      )}
      {notTracked.length > 0 && (
        <div className={`flex flex-col gap-1.5 ${needLogin.length > 0 ? "pt-2.5 border-t border-[#EEF0F4]" : ""}`}>
          <button type="button" onClick={() => setShowTracked((x) => !x)} aria-expanded={showTracked}
            className="self-start flex items-center gap-1 border-0 bg-transparent p-0 text-[12px] text-[#5B6474] cursor-pointer">
            {showTracked ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
            {notTracked.length} non-project staff not tracked (no expected hours or missed entries)
          </button>
          {showTracked && (
            <span className="text-[12px] text-[#5B6474] pl-4">
              {notTracked.map((x) => `${x.employee_name}${x.designation ? ` (${x.designation})` : ""}`).join(", ")}
            </span>
          )}
        </div>
      )}
    </section>
  );
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [entriesState, setEntriesState] = useState("loading"); // loading | ready | error | not_configured
  const [holidays, setHolidays] = useState([]);     // [{ date, name, type: General | Restricted }]
  const [leaveDates, setLeaveDates] = useState([]); // default-row (leave) days
  const [hrDates, setHrDates] = useState({ joiningDate: "", exitDate: "" }); // HR joining / exit dates
  const [org, setOrg] = useState(null);
  const [adminQ, setAdminQ] = useState("");   // Admin view toolbar search (Clients / Employees / Rocketlane sync)
  const [showAllHits, setShowAllHits] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);

  const today = useMemo(() => new Date(), []);
  const empId = localStorage.getItem("employee_id") || "";
  const isAdmin = ADMIN_ROLE_IDS.includes(String(localStorage.getItem("emp_role_id") || ""));

  // View lives in the URL (?view=org | ?view=admin) so links and "Back to dashboard" keep it.
  //   my    — everyone: own timesheet figures
  //   org   — admins: the same figures summed across all employees (no timesheet calendar)
  //   admin — admins: Clients + Organization overview
  const [params, setParams] = useSearchParams();
  const view = isAdmin && ["org", "admin"].includes(params.get("view")) ? params.get("view") : "my";
  const adminView = view === "admin";
  const orgView = view === "org";
  const setView = (v) => {
    const next = new URLSearchParams(params);
    if (v === "my") next.delete("view"); else next.set("view", v);
    setParams(next, { replace: true });
  };

  // Month shown in My view and Organisation view (?month=YYYY-MM; default this month).
  // MTD figures = that month; YTD = 1 Jan → end of that month. Any past month can be picked (month calendar;
  // click the year in its header to jump to another year). Future months are disabled.
  const ymOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const currentYm = ymOf(today);
  const rawMonth = params.get("month") || "";
  const selMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(rawMonth) && rawMonth >= "2000-01" && rawMonth <= currentYm ? rawMonth : currentYm;
  const isCurrentMonth = selMonth === currentYm;
  const setMonth = (ym) => {
    const next = new URLSearchParams(params);
    if (ym === currentYm) next.delete("month"); else next.set("month", ym);
    setParams(next, { replace: true });
  };
  // End of the period shown: today for this month, the last day of a past month
  const asOf = useMemo(
    () => (isCurrentMonth ? today : new Date(+selMonth.slice(0, 4), +selMonth.slice(5, 7), 0)),
    [isCurrentMonth, selMonth, today]
  );
  const selYear = asOf.getFullYear();

  // Organisation summary — loaded the first time Organisation view is opened
  // Organisation view client filter (?group=): all | freshworks (Rocketlane) | legacy (everyone else)
  const orgGroup = ["freshworks", "legacy", "unlisted"].includes(params.get("group")) ? params.get("group") : "all";
  const setOrgGroup = (g) => {
    const next = new URLSearchParams(params);
    if (g === "all") next.delete("group"); else next.set("group", g);
    setParams(next, { replace: true });
  };
  // One summary per group, loaded the first time it's shown and kept while the page is open
  const [orgCache, setOrgCache] = useState({});               // { [group]: summary }
  const [orgState, setOrgState] = useState({});               // { [group]: "loading" | "ready" | "error" }
  const orgRequested = useRef(new Set());
  const orgKey = `${orgGroup}|${selMonth}`;                   // one summary per (group, month)
  useEffect(() => {
    if (!orgView || orgRequested.current.has(orgKey)) return;
    orgRequested.current.add(orgKey);
    const key = orgKey;
    setOrgState((x) => ({ ...x, [key]: "loading" }));
    fetchOrganizationSummary(orgGroup, isCurrentMonth ? "" : selMonth)
      .then((d) => { setOrgCache((x) => ({ ...x, [key]: d })); setOrgState((x) => ({ ...x, [key]: "ready" })); })
      .catch((err) => {
        console.error("Organisation summary failed:", err);
        orgRequested.current.delete(key); // allow a retry
        setOrgState((x) => ({ ...x, [key]: "error" }));
      });
  }, [orgView, orgKey, orgGroup, selMonth, isCurrentMonth]);
  const orgStats = orgCache[orgKey] || null;
  const orgStatsState = orgState[orgKey] || "idle";
  // Last loaded summary of any group — keeps the dropdown's counts visible while another group loads
  const anyOrgStats = orgStats || Object.values(orgCache)[0] || null;
  const groupCountsAny = () => anyOrgStats?.groupCounts
    || Object.entries(adminCache).find(([k]) => k.startsWith("employees|"))?.[1]?.group_counts || null;

  // Extra / Holiday hours detail: { type: "extra" | "holiday", scope: "month" | "year" } or null
  const [detail, setDetail] = useState(null);
  const [showAllMissed, setShowAllMissed] = useState(false);
  const [missedTab, setMissedTab] = useState("missed"); // missed | ok
  const [missedSearch, setMissedSearch] = useState("");

  // Find the header slot (Header may mount after this page)
  const [headerSlot, setHeaderSlot] = useState(null);
  useEffect(() => {
    let tries = 0;
    let timer;
    const find = () => {
      const el = document.getElementById(HEADER_SLOT_ID);
      if (el) setHeaderSlot(el);
      else if (tries++ < 20) timer = setTimeout(find, 100);
    };
    find();
    return () => clearTimeout(timer);
  }, []);
  // Logged-in user's name: Microsoft sign-in account first, then common localStorage keys.
  const { accounts } = useMsal();
  const fullName =
    accounts?.[0]?.name ||
    localStorage.getItem("employee_name") ||
    localStorage.getItem("emp_name") ||
    localStorage.getItem("name") ||
    "";
  // "Rajkumar S" → "Rajkumar"; "S, Rajkumar" (Last, First) → "Rajkumar"
  const firstName = (fullName.includes(",") ? fullName.split(",")[1] : fullName).trim().split(/\s+/)[0] || "";

  useEffect(() => {
    let cancelled = false;
    setEntriesState("loading");

    // Entries first: they carry my country (India / US), which decides the holiday calendar.
    // Loaded per year: picking a month of last year loads last year's entries and holidays.
    fetchMyEntries(empId, selYear)
      .then((mine) => fetchHolidays(selYear, mine.country).then((hol) => [mine, hol]))
      .then(([mine, hol]) => {
        if (cancelled) return;
        setEntries(mine.entries);
        setHolidays(hol);
        setLeaveDates(mine.offDates);
        setHrDates({ joiningDate: mine.joiningDate, exitDate: mine.exitDate });
        setEntriesState("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof NotConfiguredError) { console.warn(err.message); setEntriesState("not_configured"); }
        else { console.error("Timesheet entries load failed:", err); setEntriesState("error"); }
      });

    return () => { cancelled = true; };
  }, [empId, selYear]);

  // ── Admin view ──
  // ?tab=clients|employees, ?period=month|year, ?group= (shared with Organisation view)
  const adminTab = ["employees", "sync"].includes(params.get("tab")) ? params.get("tab") : "clients";
  const adminPeriod = params.get("period") === "year" ? "year" : "month";
  const setParam = (key, value, fallback) => {
    const next = new URLSearchParams(params);
    if (value === fallback) next.delete(key); else next.set(key, value);
    setParams(next, { replace: true });
  };
  // Period: any month or year (?month=YYYY-MM is shared with My / Organisation view; year = that month's year)
  const adminAnchor = useMemo(() => new Date(+selMonth.slice(0, 4), +selMonth.slice(5, 7) - 1, 1), [selMonth]);
  const adminRange = useMemo(() => periodRange(adminPeriod, adminAnchor), [adminPeriod, adminAnchor]);
  const adminIsCurrent = adminPeriod === "year" ? selMonth.slice(0, 4) === currentYm.slice(0, 4) : isCurrentMonth;
  const shiftAdmin = (step) => {
    const d = new Date(adminAnchor);
    if (adminPeriod === "year") d.setFullYear(d.getFullYear() + step); else d.setMonth(d.getMonth() + step, 1);
    const ym = ymOf(d);
    setMonth(ym > currentYm ? currentYm : ym);
  };
  const adminKey = `${orgGroup}|${adminPeriod}|${adminPeriod === "year" ? selMonth.slice(0, 4) : selMonth}`;

  // Organization overview counts: once
  const orgCountsLoaded = useRef(false);
  useEffect(() => {
    if (!adminView || orgCountsLoaded.current) return;
    orgCountsLoaded.current = true;
    fetchOrgCounts(empId)
      .then((c) => setOrg(c))
      .catch((err) => { console.error("Org overview load failed:", err); orgCountsLoaded.current = false; });
  }, [adminView, empId]);

  // Clients / Employees: one load per (team, period, tab), kept while the page is open
  const [adminCache, setAdminCache] = useState({});   // { "clients|all|month": data }
  const [adminState, setAdminState] = useState({});   // { key: "loading" | "ready" | "error" }
  const adminRequested = useRef(new Set());
  useEffect(() => {
    if (!adminView || adminTab === "sync") return;   // the sync log loads its own data
    const key = `${adminTab}|${adminKey}`;
    if (adminRequested.current.has(key)) return;
    adminRequested.current.add(key);
    const range = adminRange;
    setAdminState((x) => ({ ...x, [key]: "loading" }));
    const load = adminTab === "clients" ? fetchActiveClients(range, orgGroup) : fetchEmployeeUtilization(range, orgGroup);
    load
      .then((d) => { setAdminCache((x) => ({ ...x, [key]: d })); setAdminState((x) => ({ ...x, [key]: "ready" })); })
      .catch((err) => {
        console.error(`Admin ${adminTab} load failed:`, err);
        adminRequested.current.delete(key);
        setAdminState((x) => ({ ...x, [key]: "error" }));
      });
  }, [adminView, adminTab, adminKey, adminRange, orgGroup]);
  const projects = adminCache[`clients|${adminKey}`] || [];
  const projState = adminState[`clients|${adminKey}`] || "loading";
  const employeeData = adminCache[`employees|${adminKey}`] || null;
  const employeeState = adminState[`employees|${adminKey}`] || "loading";
  const adminPeriodLabel = periodLabel(adminPeriod, adminRange) + (adminPeriod === "year" && adminIsCurrent ? " (Jan–today)" : "");

  // ── Export to Excel (Organisation view / Admin view) ──
  // Organisation: the summary shown. Admin: Clients + Employees of the period and team shown (one file; the tab
  // not opened yet is loaded for the file). exceljs is loaded only on click (dashboardExport.js).
  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const { exportOrganisation, exportAdmin } = await import("./Dashboardexport");
      if (orgView) {
        if (!orgStats) throw new Error("The organisation figures are still loading. Try again in a moment.");
        await exportOrganisation(orgStats, {
          monthYm: selMonth,
          monthLabel: new Date(+selMonth.slice(0, 4), +selMonth.slice(5, 7) - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" }),
          teamLabel: GROUP_LABEL[orgGroup], isCurrentMonth,
        });
      } else {
        const cKey = `clients|${adminKey}`, eKey = `employees|${adminKey}`;
        const [cl, em] = await Promise.all([
          adminCache[cKey] || fetchActiveClients(adminRange, orgGroup),
          adminCache[eKey] || fetchEmployeeUtilization(adminRange, orgGroup),
        ]);
        await exportAdmin(cl, em, {
          periodLabel: adminPeriodLabel,
          fileTag: adminPeriod === "year" ? selMonth.slice(0, 4) : selMonth,
          teamLabel: GROUP_LABEL[orgGroup],
        });
      }
      message.success("Excel file downloaded.");
    } catch (err) {
      console.error("Excel export failed:", err);
      message.error(err?.message || "Could not export. Try again.");
    } finally {
      setExporting(false);
    }
  };
  const exportButton = (h) => (
    <button type="button" onClick={exportExcel} disabled={exporting || (orgView && !orgStats)}
      title={orgView ? "Download this month's organisation figures as Excel" : "Download Clients, Projects and Employees for this period as Excel"}
      className={`${h} px-3.5 rounded-[10px] border border-[#1E6B52] bg-white text-[13px] font-medium text-[#1E6B52] cursor-pointer whitespace-nowrap flex items-center gap-2 hover:bg-[#EEF8F3] disabled:opacity-50 disabled:cursor-default`}>
      <FiDownload aria-hidden="true" className="text-[15px]" /> {exporting ? "Exporting…" : "Export Excel"}
    </button>
  );

  const mine = useMemo(() => {
    const st = buildDashboardStats(entries, { today, asOf, holidays, leaveDates, ...hrDates });
    return { ...st, missedCount: st.missedDays.length };
  }, [entries, today, asOf, holidays, leaveDates, hrDates]);
  // Placeholder while the organisation summary loads (same shape, all zeros)
  const EMPTY = useMemo(() => ({
    mtd: { billable: 0, nonBillable: 0, logged: 0, expected: 0, regular: 0, extra: 0, holiday: 0, notLogged: 0, workingDays: 0, billablePct: 0, utilization: 0 },
    ytd: { billable: 0, nonBillable: 0, logged: 0, expected: 0, regular: 0, extra: 0, holiday: 0, billablePct: 0, utilization: 0, missedDays: 0 },
    missedCount: 0, missedDays: [], holidays: [], extraDays: [], holidayDays: [], months: [], projects: [], employees: [], employeeCount: 0,
    rocketlaneUnmatched: [], notInTimesheet: [],
  }), []);
  const s = orgView ? (orgStats || EMPTY) : mine;
  const loading = orgView ? orgStatsState !== "ready" && orgStatsState !== "error" : entriesState === "loading";
  const v = (x) => (loading ? "…" : x);

  const goFill = (iso) => navigate(iso ? `/timesheets?date=${iso}` : "/timesheets");
  // Client / FPS click → Utilization page, same period (month or year) as here
  const utilUrl = (extra) => `/utilization?mode=${adminPeriod}&date=${toISO(adminAnchor)}${extra}&from=dashboard`;
  const openClient = (id, bill) => navigate(utilUrl(`&client=${id}${bill ? `&bill=${bill}` : ""}`));
  const openFps = () => navigate(utilUrl("&fps=1"));
  const openProject = (cid, pid) => navigate(utilUrl(`&client=${cid}&project=${pid}`));

  const PROJECTS_PREVIEW = 8;
  const projQuery = adminQ.trim().toLowerCase();
  const projFiltered = projQuery
    ? projects.filter((p) => (p.client_name || "").toLowerCase().includes(projQuery) || (projQuery === "fps" && p.fps_projects_count > 0)
        || (p.projects || []).some((x) => (x.project_name || "").toLowerCase().includes(projQuery)))
    : projects;
  // Same search box, Clients tab: projects of every client whose name matches; "fps" alone lists FPS projects
  const projSearchQ = adminTab === "clients" ? projQuery : "";
  const projectHits = projSearchQ
    ? projects.flatMap((c) => (c.projects || []).map((p) => ({ ...p, client_id: c.client_id, client_name: c.client_name })))
        .filter((p) => (p.project_name || "").toLowerCase().includes(projSearchQ) || (projSearchQ === "fps" && (p.fps ?? isFps(p.project_name))))
        .sort((a, b) => b.hours - a.hours || (a.project_name || "").localeCompare(b.project_name || ""))
    : [];
  const HITS_PREVIEW = 15;
  // FPS projects across all clients (name contains "FPS"); normal = everything else
  const fpsSum = projects.reduce((t, c) => ({
    h: t.h + (Number(c.fps_hours) || 0), b: t.b + (Number(c.fps_billable_hours) || 0),
    n: t.n + (Number(c.fps_projects_count) || 0), withH: t.withH + (Number(c.fps_projects_with_hours) || 0),
    clients: t.clients + (c.fps_projects_count > 0 ? 1 : 0), total: t.total + (Number(c.total_hours) || 0),
  }), { h: 0, b: 0, n: 0, withH: 0, clients: 0, total: 0 });
  const projShown = showAllProjects || projQuery ? projFiltered : projFiltered.slice(0, PROJECTS_PREVIEW);
  const maxProjectHours = Math.max(1, ...projects.map((p) => p.total_hours || 0));
  const monthName = asOf.toLocaleDateString("en-GB", { month: "long" });
  const monthYear = asOf.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const mtdTag = isCurrentMonth ? "MTD" : asOf.toLocaleDateString("en-GB", { month: "short", year: "2-digit" }); // "MTD" | "Jul 26"
  const inMonthText = isCurrentMonth ? "this month" : `in ${monthName}`;

  // Stacked progress bar for this month (scaled to whichever is bigger: expected or logged)
  // Bar = billable + non-billable (all logged, incl. extra) + not-logged shortfall
  const barBase = (s.mtd.logged + s.mtd.notLogged) || 1;
  const pct = (n) => `${(n / barBase) * 100}%`;
  const maxProject = s.projects[0]?.hours || 1;
  const maxMonth = Math.max(1, ...s.months.map((m) => m.billable + m.nonBillable));
  const CHART_PX = 170;

  const actions = (
    <div className="flex flex-wrap items-center gap-2.5" style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
      {isAdmin && (
        <div role="tablist" aria-label="Dashboard view" className="flex bg-white border border-[#D5DAE3] rounded-[10px] p-1">
          {[
            { id: "my", label: "My view", Icon: FiUser },
            { id: "org", label: "Organisation view", Icon: FiGlobe },
            { id: "admin", label: "Admin view", Icon: FiShield },
          ].map(({ id, label, Icon }) => (
            <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => setView(id)}
              className={`min-h-[32px] px-3 rounded-lg border-0 text-[13px] cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${view === id ? "bg-[#E3ECFB] text-[#164AA6] font-semibold" : "bg-transparent text-[#3D4555] hover:bg-[#F4F6FA]"}`}>
              <Icon aria-hidden="true" />{label}
            </button>
          ))}
        </div>
      )}
      {!adminView && (
        <div className="flex items-center gap-2 text-[13px] text-[#3D4555]">
          <span className="whitespace-nowrap">Month</span>
          {/* Month calendar: click the year at the top to pick another year, then the month */}
          <DatePicker
            picker="month"
            value={dayjs(`${selMonth}-01`)}
            format="MMMM YYYY"
            allowClear={false}
            inputReadOnly
            aria-label="Month to show"
            style={{ width: 170, height: 40, borderRadius: 10 }}
            disabledDate={(d) => d && d.isAfter(dayjs(), "month")}
            onChange={(d) => d && setMonth(d.format("YYYY-MM"))}
            renderExtraFooter={() =>
              !isCurrentMonth ? (
                <button type="button" onClick={() => setMonth(currentYm)}
                  className="w-full py-1 border-0 bg-transparent text-[13px] text-[#1D5FD1] cursor-pointer">
                  Back to this month
                </button>
              ) : null
            }
          />
          {!isCurrentMonth && (
            <button type="button" onClick={() => setMonth(currentYm)}
              className="min-h-[40px] px-3 rounded-[10px] border border-[#D5DAE3] bg-white text-[13px] text-[#1D5FD1] cursor-pointer whitespace-nowrap hover:bg-[#F8FAFC]">
              This month
            </button>
          )}
        </div>
      )}
      <button type="button" onClick={() => navigate("/timesheets")}
        className="min-h-[40px] px-4 rounded-[10px] border border-[#D5DAE3] bg-white text-[14px] font-medium text-[#0F172A] cursor-pointer hover:bg-[#F8FAFC]">
        View timesheets
      </button>
      <button type="button" onClick={() => goFill(toISO(today))}
        className="min-h-[40px] px-4 rounded-[10px] border-0 bg-[#1D5FD1] hover:bg-[#164AA6] text-white text-[14px] font-semibold cursor-pointer flex items-center gap-2">
        <FiPlus aria-hidden="true" className="text-[16px]" /> Log time today
      </button>
    </div>
  );

  return (
    <div className={`pt-10 px-[clamp(12px,2.5vw,40px)] pb-12 bg-[#F4F6FA] min-h-screen text-[#0F172A]`} style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
      {/* Admin view uses the full width on big screens (up to 1680px) and wraps on laptops; other views stay at 1180px */}
      <div className={`${adminView ? "max-w-[1680px] gap-4" : "max-w-[1180px] gap-6"} mx-auto flex flex-col`}>

        {/* Greeting (top-left). Buttons go to the header bar, next to "Hi, <name>". Admin view: no greeting (more room for data) */}
        {(!adminView || !headerSlot) && (
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <p className="m-0 text-[13px] text-[#5B6474]">
              {longDate(today)}
              {view !== "my" && (
                <span className="ml-2 align-middle text-[11px] font-semibold text-[#164AA6] bg-[#E3ECFB] rounded-full px-2 py-0.5">
                  {orgView
                    ? `Organisation view${orgGroup !== "all" ? ` · ${GROUP_LABEL[orgGroup]}` : ""}${orgStats ? ` · ${orgStats.employeeCount} employees` : ""}`
                    : "Admin view"}
                </span>
              )}
            </p>
            <h1 className="m-0 text-[28px] font-semibold tracking-[-0.01em]">
              {greeting()}{firstName ? `, ${firstName}` : ""}
            </h1>
          </div>
          {/* Fallback when Header.js has no slot yet */}
          {!headerSlot && actions}
          {/* Organisation view: Export under the header buttons, on the right */}
          {orgView && <div className="ml-auto self-start">{exportButton("min-h-[40px]")}</div>}
        </header>
        )}
        {headerSlot && createPortal(actions, headerSlot)}

        {detail && (
          <HoursDetailModal
            type={detail.type}
            scope={detail.scope}
            setScope={(sc) => setDetail((x) => ({ ...x, scope: sc }))}
            rows={(detail.type === "extra" ? s.extraDays : s.holidayDays)
              .filter((r) => detail.scope === "year" || (r.date.getMonth() === asOf.getMonth() && r.date.getFullYear() === asOf.getFullYear()))}
            showEmployee={orgView}
            monthName={monthName}
            onClose={() => setDetail(null)}
          />
        )}

        {/* Team filter (Organisation view; Admin view has it in its toolbar) + data notes */}
        {orgView && (
          <section aria-label="Team filter" className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label className="flex items-center gap-2 text-[13px] text-[#3D4555]">
              <span className="font-medium">Team</span>
              <select value={orgGroup} onChange={(e) => setOrgGroup(e.target.value)}
                className="h-10 pl-3 pr-8 rounded-[10px] border border-[#D5DAE3] bg-white text-[14px] text-[#0F172A] cursor-pointer focus:outline-none focus:border-[#1D5FD1]">
                {["all", "freshworks", "legacy", "unlisted"].map((g) => {
                  const n = groupCountsAny()?.[g];
                  return <option key={g} value={g}>{GROUP_LABEL[g]}{n != null ? ` (${n})` : ""}</option>;
                })}
              </select>
            </label>
            <span className="text-[12px] text-[#5B6474]">
              {orgGroup === "freshworks" && "People who log time in Rocketlane — their Activity Card hours are included too."}
              {orgGroup === "legacy" && "Everyone who doesn't log time in Rocketlane."}
              {orgGroup === "all" && "Freshworks (Rocketlane) and Legacy together."}
              {orgGroup === "unlisted" && "Rocketlane users HR hasn't listed (added by hand, or found only in Rocketlane). Not included in All employees."}
              {orgView && orgStats && orgGroup !== "unlisted" && (orgStats.employeeSource === "hr_list"
                ? " Counted from their date of joining (HR employee list)."
                : " HR employee list not loaded — counting active timesheet accounts.")}
            </span>
          </section>
        )}

        {/* ── My view (everyone) and Organisation view (admins) share this UI ── */}
        {!adminView && (<>
        {orgView && orgGroup !== "unlisted" && orgStats?.rocketlaneUnmatched?.length > 0 && (
          <div role="status" className="rounded-[12px] border border-[#F6C9A8] bg-[#FFF4EC] px-4 py-3 text-[13px] text-[#7C2D12] flex flex-wrap items-center justify-between gap-3">
            <span className="flex flex-col gap-1 min-w-0">
              <strong className="text-[#9A3412]">
                {orgStats.rocketlaneUnmatched.length} Rocketlane user{orgStats.rocketlaneUnmatched.length > 1 ? "s aren't" : " isn't"} on the HR list
                — {fmt(orgStats.rocketlaneUnmatched.reduce((a, u) => a + Number(u.hours || 0), 0))} h not in these figures
              </strong>
              <span>
                {orgStats.rocketlaneUnmatched.slice(0, 6).map((u) => `${u.name || u.email} (${fmt(u.hours)} h)`).join(" · ")}
                {orgStats.rocketlaneUnmatched.length > 6 ? ` · +${orgStats.rocketlaneUnmatched.length - 6} more` : ""}
              </span>
              <span className="text-[12px]">If they're on the HR list under another email, map them in RL_User_Map (Emp_Code).</span>
            </span>
            <button type="button" onClick={() => setOrgGroup("unlisted")}
              className="min-h-[36px] px-3 rounded-[10px] border border-[#F6C9A8] bg-white text-[#9A3412] text-[13px] font-semibold cursor-pointer hover:bg-[#FFF4EC] whitespace-nowrap">
              See their hours
            </button>
          </div>
        )}

        {orgView && orgGroup !== "unlisted" && orgStats && (
          <PeopleNote needLogin={orgStats.notInTimesheet} notTracked={orgStats.notTracked} />
        )}

        {orgView && orgStatsState === "error" && (
          <div role="alert" className="rounded-[12px] border border-[#F6C9A8] bg-[#FFF4EC] px-4 py-3 text-[13px] text-[#7C2D12]">
            Couldn't load the organisation summary. Try refreshing.
          </div>
        )}
        {!orgView && entriesState === "not_configured" && (
          <div role="status" className="rounded-[12px] border border-[#D5DAE3] bg-white px-4 py-3 text-[13px] text-[#5B6474]">
            Personal timesheet figures aren't connected yet (set <code>myTimesheetEntries</code> in dashboardApi.js).
          </div>
        )}

        {!orgView && entriesState === "error" && (
          <div role="alert" className="rounded-[12px] border border-[#F6C9A8] bg-[#FFF4EC] px-4 py-3 text-[13px] text-[#7C2D12]">
            Couldn't load your timesheet entries. The figures below may be incomplete — try refreshing.
          </div>
        )}

        {/* Missed timesheet alert */}
        {!orgView && !loading && s.missedDays.length > 0 && (
          <section aria-label="Missed timesheets alert" className="flex flex-wrap items-center gap-4 bg-[#FFF4EC] border border-[#F6C9A8] rounded-[14px] px-5 py-4">
            <div aria-hidden="true" className="w-10 h-10 rounded-full bg-[#FDE6D6] text-[#C2410C] flex items-center justify-center shrink-0">
              <FiAlertTriangle className="text-[20px]" />
            </div>
            <div className="flex-[1_1_320px] flex flex-col gap-0.5">
              <strong className="text-[15px] text-[#9A3412]">
                You have {s.missedDays.length} missed timesheet{s.missedDays.length > 1 ? "s" : ""} {inMonthText}
              </strong>
              <span className="text-[13px] text-[#7C2D12]">
                {s.missedDays.map((m) => shortDate(m.date)).join(" · ")} — {fmt(s.missedDays.length * HOURS_PER_DAY)} h not logged.
                {" "}Submit before the month closes on {shortDate(s.monthClose)}.
              </span>
            </div>
            <button type="button" onClick={() => goFill(s.missedDays[0].iso)}
              className="min-h-[44px] px-[18px] rounded-[10px] border-0 bg-[#C2410C] hover:bg-[#9A3412] text-white text-[14px] font-semibold cursor-pointer">
              Fill missed days
            </button>
          </section>
        )}

        {/* KPI tiles */}
        <section aria-label="Key figures" className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
          <Kpi label={`${mtdTag} billable`} swatch={C.primary} value={v(fmt(s.mtd.billable))} unit="h"
               sub={`${fmt(s.mtd.billablePct)}% of hours logged`} />
          <Kpi label={`${mtdTag} non-billable`} swatch={C.nonBill} value={v(fmt(s.mtd.nonBillable))} unit="h"
               sub="Meetings, training, internal" />
          <Kpi label={orgView ? "Missed entries" : "Missed timesheets"} swatch="#EA7A3A" warn={s.missedCount > 0}
               value={v(fmt(s.missedCount))} unit={orgView ? inMonthText : "days"}
               sub={orgView
                 ? `${fmt(s.ytd.missedDays)} missed year to date · all employees`
                 : `${s.ytd.missedDays} missed year to date`} />
          <Kpi label={`${mtdTag} utilization`} value={v(fmt(s.mtd.utilization))} unit="%"
               sub={`${fmt(s.mtd.logged)} of ${fmt(s.mtd.expected)} h expected${s.mtd.extra ? ` · incl. ${fmt(s.mtd.extra)} h extra` : ""}${s.mtd.holiday ? ` · ${fmt(s.mtd.holiday)} h on holidays` : ""}`} />
          <Kpi label="Holiday hours" swatch={C.holiday} value={v(fmt(s.mtd.holiday))} unit="h"
               onClick={loading ? undefined : () => setDetail({ type: "holiday", scope: "month" })}
               sub={holidaySub(s.holidays, orgView)} />
          <Kpi label="YTD billable / non-billable" value={v(fmt(s.ytd.billable))} unit={`/ ${fmt(s.ytd.nonBillable)} h`}
               sub={`${fmt(s.ytd.billablePct)}% billable · Jan–${asOf.toLocaleDateString("en-GB", { month: "short" })}`} />
        </section>

        {/* This month + timesheet status */}
        <section className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(340px,1fr))]">

          {/* This month */}
          <Card as="article" className="p-6 flex flex-col gap-5">
            <div className="flex flex-wrap justify-between items-baseline gap-2">
              <h2 className="m-0 text-[18px] font-semibold">{isCurrentMonth ? `${monthName} so far` : monthYear}</h2>
              <span className="text-[13px] text-[#5B6474]">
                {orgView ? `${s.employeeCount} employees · ` : ""}{s.mtd.workingDays} working days to date
              </span>
            </div>
            {s.holidays.length > 0 && (
              <p className="m-0 -mt-3 text-[12px] text-[#5B21B6] flex flex-wrap items-center gap-x-2 gap-y-1">
                <Swatch color={C.holiday} />
                {s.holidays.map((h) => `${h.name} · ${shortDate(isoDate(h.date))}${h.type === "Restricted" ? " (restricted)" : ""}`).join("  ·  ")}
                <span className="text-[#5B6474]">— not counted in expected hours</span>
              </p>
            )}
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-[40px] font-semibold leading-none">{v(fmt(s.mtd.logged))}</span>
              <span className="text-[15px] text-[#5B6474]">of {fmt(s.mtd.expected)} h expected logged · {fmt(s.mtd.utilization)}% utilization</span>
            </div>
            <div role="img"
              aria-label={`Billable ${fmt(s.mtd.billable)} hours, non-billable ${fmt(s.mtd.nonBillable)} hours, not logged ${fmt(s.mtd.notLogged)} hours, out of ${fmt(s.mtd.expected)} expected`}
              className="flex h-4 rounded-full overflow-hidden bg-[#F1F3F7] gap-0.5">
              <div style={{ width: pct(s.mtd.billable), background: C.primary }} />
              <div style={{ width: pct(s.mtd.nonBillable), background: C.nonBill }} />
              <div style={{ width: pct(s.mtd.notLogged), background: `repeating-linear-gradient(135deg, ${C.warnSoft} 0 6px, ${C.warnBorder} 6px 9px)` }} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Legend color={C.primary} label="Billable" value={`${fmt(s.mtd.billable)} h`} />
              <Legend color={C.nonBill} label="Non-billable" value={`${fmt(s.mtd.nonBillable)} h`} />
              <DetailFigure label="Extra hours" title="Saturday / Sunday / leave-day hours and hours above 8 h on a working day"
                value={`${fmt(s.mtd.extra)} h`} onClick={() => setDetail({ type: "extra", scope: "month" })} />
              <DetailFigure label="Holiday hours" title="Hours logged on a company holiday (kept separate from extra hours)"
                value={`${fmt(s.mtd.holiday)} h`} onClick={() => setDetail({ type: "holiday", scope: "month" })} />
              <Legend color={C.warnBorder} label="Not logged" value={`${fmt(s.mtd.notLogged)} h`} warn />
            </div>

            <div className="border-t border-[#EEF0F4] pt-4 flex flex-col gap-2.5">
              <h3 className="m-0 text-[14px] font-semibold text-[#3D4555]">Hours by project</h3>
              {s.projects.length === 0 && <p className="m-0 text-[13px] text-[#5B6474]">{loading ? "Loading…" : (isCurrentMonth ? "No hours logged this month yet." : `No hours logged in ${monthName}.`)}</p>}
              <div className="flex flex-col gap-2.5 max-h-[260px] overflow-y-auto pr-1">
                {s.projects.map((p) => (
                  <div key={p.name} className="grid grid-cols-[minmax(0,1fr)_64px] gap-x-3 gap-y-1 items-center">
                    <span className="text-[13px] truncate" title={p.name}>{p.name}</span>
                    <span className="text-[13px] font-semibold text-right">{p.hours.toFixed(1)} h</span>
                    <div className="col-span-2 h-1.5 bg-[#F1F3F7] rounded-full">
                      <div className="h-1.5 rounded-full"
                        style={{ width: `${(p.hours / maxProject) * 100}%`, background: p.billableHours >= p.hours / 2 ? C.primary : C.nonBill }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Organisation view: missed entries by employee (instead of the personal calendar) */}
          {orgView && (
            <Card as="article" className="p-6 flex flex-col gap-4">
              <div className="flex flex-wrap justify-between items-baseline gap-2">
                <h2 className="m-0 text-[18px] font-semibold">{orgGroup === "unlisted" ? "Hours by person" : "Missed entries by employee"}</h2>
                <span className="text-[13px] text-[#5B6474]">{monthYear}</span>
              </div>
              {loading ? (
                <div className="flex flex-col gap-2">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-10 rounded-lg bg-[#F1F3F7] animate-pulse" />)}</div>
              ) : orgGroup === "unlisted" ? (
                // "Not on HR list" (e.g. pmo@ shared mailbox): no joining date from HR → hours only, no missed entries
                <div className="flex flex-col gap-2">
                  <p className="m-0 text-[12px] text-[#5B6474]">
                    Rocketlane users HR hasn't listed. Their hours are shown; missed entries aren't tracked for them.
                  </p>
                  {s.employees.length === 0 ? (
                    <p className="m-0 text-[13px] text-[#5B6474]">No one in this group.</p>
                  ) : (
                    <div className="flex flex-col">
                      {[...s.employees].sort((a, b) => (b.logged_mtd_hours || 0) - (a.logged_mtd_hours || 0)).map((e) => (
                        <div key={e.employee_id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-center py-2 border-b border-[#F1F3F7] last:border-0">
                          <span className="flex flex-col min-w-0">
                            <span className="text-[14px] font-medium truncate">{e.employee_name}</span>
                            <span className="text-[11px] text-[#5B6474]">
                              {e.last_logged ? `last entry ${shortDate(new Date(e.last_logged + "T00:00:00"))}` : "no entries this year"}
                            </span>
                          </span>
                          <span className="text-right text-[13px]"><strong>{fmt(e.logged_mtd_hours)} h</strong> <span className="text-[#5B6474]">{inMonthText}</span></span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (() => {
                // The API returns everyone employed at any point this year (needed for YTD numbers).
                // This list is about the current month, so keep only people still employed on/after the 1st,
                // plus anyone who logged hours this month (e.g. left mid-month).
                const monthStartStr = `${selMonth}-01`;
                const inMonth = s.employees.filter((e) =>
                  !e.date_of_exit || e.date_of_exit >= monthStartStr || e.logged_mtd_hours > 0);
                const withMissed = inMonth.filter((e) => e.missed_mtd > 0);
                const upToDate = inMonth.filter((e) => e.missed_mtd === 0);
                const mq = missedSearch.trim().toLowerCase();
                const rows = (missedTab === "missed" ? withMissed : upToDate)
                  .filter((e) => !mq || (e.employee_name || "").toLowerCase().includes(mq));
                const list = showAllMissed || mq ? rows : rows.slice(0, 8);
                const maxMissed = Math.max(1, ...withMissed.map((e) => e.missed_mtd));
                const tabCls = (on) => `h-8 px-3 rounded-lg border-0 text-[12px] cursor-pointer ${on ? "bg-[#E3ECFB] text-[#164AA6] font-semibold" : "bg-transparent text-[#3D4555] hover:bg-[#F4F6FA]"}`;
                return (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                    <div role="tablist" aria-label="Employees" className="flex bg-[#F4F6FA] rounded-[10px] p-1 gap-1">
                      <button type="button" role="tab" aria-selected={missedTab === "missed"} className={tabCls(missedTab === "missed")}
                        onClick={() => { setMissedTab("missed"); setShowAllMissed(false); }}>
                        Missed entries ({withMissed.length})
                      </button>
                      <button type="button" role="tab" aria-selected={missedTab === "ok"} className={tabCls(missedTab === "ok")}
                        onClick={() => { setMissedTab("ok"); setShowAllMissed(false); }}>
                        Up to date ({upToDate.length})
                      </button>
                    </div>
                    <label className="relative">
                      <FiSearch aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93A3]" />
                      <input type="search" value={missedSearch} onChange={(e) => { setMissedSearch(e.target.value); setShowAllMissed(false); }}
                        placeholder="Search employee" aria-label="Search employee"
                        className="h-9 w-[200px] pl-9 pr-3 rounded-[10px] border border-[#E6E9EF] text-[13px] box-border" />
                    </label>
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] text-[12px] text-[#5B6474] border-b border-[#EEF0F4] pb-1.5">
                      <span>
                        {missedTab === "missed"
                          ? `${withMissed.length} of ${inMonth.length} employees have missed entries ${inMonthText}`
                          : `${upToDate.length} of ${inMonth.length} employees have no missed entries ${inMonthText}`}
                      </span>
                      <span>{missedTab === "missed" ? `${isCurrentMonth ? "This month" : monthName} · YTD` : "Missed YTD"}</span>
                    </div>
                    {rows.length === 0 ? (
                      <p className="m-0 text-[13px] text-[#5B6474]">
                        {mq ? `No employee matches "${missedSearch}".`
                          : missedTab === "missed" ? `Everyone is up to date ${inMonthText}.` : `No one is fully up to date ${inMonthText}${isCurrentMonth ? " yet" : ""}.`}
                      </p>
                    ) : (
                    <div className="flex flex-col max-h-[420px] overflow-y-auto pr-1">
                      {list.map((e) => (
                        <div key={e.employee_id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 items-center py-2 border-b border-[#F1F3F7] last:border-0">
                          <span className="flex flex-col min-w-0">
                            <span className="text-[14px] font-medium truncate">{e.employee_name}</span>
                            <span className="text-[11px] text-[#5B6474]">
                              {fmt(e.logged_mtd_hours)} h logged {inMonthText}{e.extra_mtd_hours > 0 || e.holiday_mtd_hours > 0 ? ` (${[e.extra_mtd_hours > 0 && `${fmt(e.extra_mtd_hours)} h extra`, e.holiday_mtd_hours > 0 && `${fmt(e.holiday_mtd_hours)} h on holidays`].filter(Boolean).join(", ")})` : ""}{e.last_logged ? ` · last entry ${shortDate(new Date(e.last_logged + "T00:00:00"))}` : " · no entries this year"}
                            </span>
                          </span>
                          {missedTab === "missed" ? (
                            <span className="text-right">
                              <span className="text-[14px] font-semibold text-[#C2410C]">{e.missed_mtd}</span>
                              <span className="text-[12px] text-[#5B6474]"> · {e.missed_ytd}</span>
                            </span>
                          ) : (
                            <span className={`text-right text-[13px] ${e.missed_ytd > 0 ? "text-[#5B6474]" : "text-[#1E6B52] font-semibold"}`}>
                              {e.missed_ytd > 0 ? e.missed_ytd : "None"}
                            </span>
                          )}
                          {missedTab === "missed" && (
                            <span className="col-span-2 h-1 bg-[#F1F3F7] rounded-full overflow-hidden">
                              <span className="block h-1 rounded-full" style={{ width: `${(e.missed_mtd / maxMissed) * 100}%`, background: "#EA7A3A" }} />
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    )}
                    {!mq && rows.length > 8 && (
                      <button type="button" onClick={() => setShowAllMissed((x) => !x)}
                        className="self-start h-9 px-3 rounded-[10px] border-0 bg-transparent text-[13px] font-semibold text-[#1D5FD1] cursor-pointer hover:bg-[#EEF3FC]">
                        {showAllMissed ? "Show fewer" : `Show all ${rows.length}`}
                      </button>
                    )}
                  </>
                );
              })()}
            </Card>
          )}

          {/* Timesheet status calendar — My view only */}
          {!orgView && (
          <Card as="article" className="p-6 flex flex-col gap-[18px]">
            <div className="flex flex-wrap justify-between items-baseline gap-2">
              <h2 className="m-0 text-[18px] font-semibold">Timesheet status</h2>
              <span className="text-[13px] text-[#5B6474]">{monthYear}</span>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="grid grid-cols-7 gap-1.5" aria-hidden="true">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <span key={d} className="text-[11px] text-[#5B6474] text-center">{d}</span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1.5" role="list" aria-label={`${monthYear} days`}>
                {s.cells.map((c) => {
                  const clickable = ["missed", "today", "logged"].includes(c.status)
                    || (["holiday", "restricted"].includes(c.status) && c.iso <= toISO(today));
                  const title = c.day
                    ? `${shortDate(isoDate(c.iso))}: ${c.holiday ? `${c.holiday.name} (${c.holiday.type === "Restricted" ? "restricted holiday" : "holiday"})` : CELL_LABEL[c.status]}${c.hours ? ` · ${fmt(c.hours)} h logged` : ""}`
                    : undefined;
                  return (
                    <div key={c.key} role="listitem" title={title}
                      onClick={clickable ? () => goFill(c.iso) : undefined}
                      className={`relative h-[34px] rounded-lg flex items-center justify-center text-[12px] font-medium box-border ${CELL[c.status]} ${clickable ? "cursor-pointer hover:brightness-95" : ""}`}>
                      {c.day || ""}
                      {/* Holiday that was worked (or a Restricted one worked as normal): small violet dot */}
                      {c.holiday && c.hours > 0 && (
                        <span aria-hidden="true" className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full" style={{ background: C.holiday }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap gap-3.5 text-[12px] text-[#3D4555]">
              <span className="flex items-center gap-1.5"><span aria-hidden="true" className="w-3 h-3 rounded bg-[#E3ECFB]" />Logged</span>
              <span className="flex items-center gap-1.5"><span aria-hidden="true" className="w-3 h-3 rounded bg-[#FDE6D6] border-[1.5px] border-[#EA7A3A] box-border" />Missed</span>
              <span className="flex items-center gap-1.5"><span aria-hidden="true" className="w-3 h-3 rounded border-2 border-[#1D5FD1] box-border" />Today</span>
              <span className="flex items-center gap-1.5"><span aria-hidden="true" className="w-3 h-3 rounded bg-[#F1F3F7]" />Weekend</span>
              <span className="flex items-center gap-1.5"><span aria-hidden="true" className="w-3 h-3 rounded bg-[#DDD6FE]" />Holiday</span>
              <span className="flex items-center gap-1.5"><span aria-hidden="true" className="w-3 h-3 rounded bg-[#F5F3FF] border border-dashed border-[#A78BFA] box-border" />Restricted holiday</span>
              {leaveDates.length > 0 && <span className="flex items-center gap-1.5"><span aria-hidden="true" className="w-3 h-3 rounded bg-[#ECFDF5]" />Leave</span>}
            </div>

            <div className="border-t border-[#EEF0F4] pt-4 flex flex-col gap-1">
              <h3 className="m-0 mb-1.5 text-[14px] font-semibold text-[#3D4555]">Needs your attention</h3>
              {!loading && s.missedDays.length === 0 && (
                <p className="m-0 text-[13px] text-[#5B6474]">You're all caught up {inMonthText}.</p>
              )}
              {s.missedDays.map((m, i) => (
                <div key={m.iso} className={`flex items-center justify-between gap-3 py-2 ${i < s.missedDays.length - 1 ? "border-b border-[#F1F3F7]" : ""}`}>
                  <div className="flex flex-col">
                    <span className="text-[14px] font-medium">{shortDate(m.date)}</span>
                    <span className="text-[12px] text-[#5B6474]">No entries · {m.daysOverdue} day{m.daysOverdue === 1 ? "" : "s"} overdue</span>
                  </div>
                  <button type="button" onClick={() => goFill(m.iso)}
                    className="min-h-[44px] px-3.5 rounded-[10px] border border-[#F6C9A8] bg-white text-[#9A3412] text-[13px] font-semibold cursor-pointer hover:bg-[#FFF4EC]">
                    Fill now
                  </button>
                </div>
              ))}
            </div>
          </Card>
          )}
        </section>

        {/* Year to date */}
        <Card as="article" className="p-6 flex flex-col gap-5">
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="m-0 text-[18px] font-semibold">Year to date</h2>
              <span className="text-[13px] text-[#5B6474]">
                January – {monthYear} · hours per month
              </span>
            </div>
            <div className="flex flex-wrap gap-7">
              <div className="flex flex-col gap-0.5">
                <span className="text-[12px] text-[#5B6474] flex items-center gap-1.5"><Swatch color={C.primary} />Billable</span>
                <span className="text-[22px] font-semibold">{v(fmt(s.ytd.billable))} h</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[12px] text-[#5B6474] flex items-center gap-1.5"><Swatch color={C.nonBill} />Non-billable</span>
                <span className="text-[22px] font-semibold">{v(fmt(s.ytd.nonBillable))} h</span>
              </div>
              <DetailFigure big label="Extra" title="Saturday / Sunday / leave-day hours and hours above 8 h on a working day"
                value={v(`${fmt(s.ytd.extra)} h`)} onClick={() => setDetail({ type: "extra", scope: "year" })} />
              <DetailFigure big label="Holiday" swatch={C.holiday} title="Hours logged on company holidays"
                value={v(`${fmt(s.ytd.holiday)} h`)} onClick={() => setDetail({ type: "holiday", scope: "year" })} />
              <div className="flex flex-col gap-0.5">
                <span className="text-[12px] text-[#5B6474]" title="Logged hours ÷ expected hours (working days × 8 h, holidays excluded)">Utilization</span>
                <span className="text-[22px] font-semibold">{v(`${fmt(s.ytd.utilization)}%`)}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[12px] text-[#5B6474]">Billable %</span>
                <span className="text-[22px] font-semibold">{v(`${fmt(s.ytd.billablePct)}%`)}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[12px] text-[#9A3412]">{orgView ? "Missed entries" : "Missed days"}</span>
                <span className="text-[22px] font-semibold text-[#C2410C]">{v(fmt(s.ytd.missedDays))}</span>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <div role="img" aria-label={`Monthly billable and non-billable hours, January to ${monthYear}`}
              className="min-w-[560px] grid gap-3.5 items-end h-[220px] border-b border-[#E6E9EF] px-1"
              style={{ gridTemplateColumns: `repeat(${s.months.length}, minmax(0, 1fr))` }}>
              {s.months.map((m) => {
                const total = m.billable + m.nonBillable;
                return (
                  <div key={m.label} className="flex flex-col items-center justify-end gap-1.5 h-full">
                    <span className="text-[11px] text-[#5B6474] font-medium">{total ? fmt(total) : ""}</span>
                    <div className="w-full max-w-[44px] flex flex-col gap-0.5">
                      {m.nonBillable > 0 && (
                        <div className="rounded-t-md" style={{ height: (m.nonBillable / maxMonth) * CHART_PX, background: C.nonBill }} />
                      )}
                      <div className={m.nonBillable > 0 ? "" : "rounded-t-md"}
                        style={{ height: (m.billable / maxMonth) * CHART_PX, background: C.primary }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="min-w-[560px] grid gap-3.5 pt-2 px-1"
              style={{ gridTemplateColumns: `repeat(${s.months.length}, minmax(0, 1fr))` }}>
              {s.months.map((m) => (
                <span key={m.label} className="text-[12px] text-[#5B6474] text-center">{m.label}</span>
              ))}
            </div>
          </div>
        </Card>

        </>)}

        {/* ── Admin view ── */}
        {adminView && (<>
        {/* One toolbar: tab · team · search | period (month / year, any past month) */}
        <div className="relative z-[1] flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <div role="tablist" aria-label="Admin view" className="flex bg-white border border-[#D5DAE3] rounded-[10px] p-1">
              {[{ id: "clients", label: "Clients" }, { id: "employees", label: "Employees" }, { id: "sync", label: "Rocketlane sync" }].map((t) => (
                <button key={t.id} type="button" role="tab" aria-selected={adminTab === t.id} onClick={() => setParam("tab", t.id, "clients")}
                  className={`min-h-[32px] px-3.5 rounded-lg border-0 text-[13px] cursor-pointer whitespace-nowrap ${adminTab === t.id ? "bg-[#E3ECFB] text-[#164AA6] font-semibold" : "bg-transparent text-[#3D4555] hover:bg-[#F4F6FA]"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            {adminTab !== "sync" && (
              <select value={orgGroup} onChange={(e) => setOrgGroup(e.target.value)} aria-label="Team"
                title={{ all: "Freshworks (Rocketlane) and Legacy together.", freshworks: "People who log time in Rocketlane — their Activity Card hours are included too.",
                  legacy: "Everyone who doesn't log time in Rocketlane.", unlisted: "Rocketlane users HR hasn't listed. Not included in All employees." }[orgGroup]}
                className="h-10 pl-3 pr-8 rounded-[10px] border border-[#D5DAE3] bg-white text-[13px] text-[#0F172A] cursor-pointer focus:outline-none focus:border-[#1D5FD1]">
                {["all", "freshworks", "legacy", "unlisted"].map((g) => {
                  const n = groupCountsAny()?.[g];
                  return <option key={g} value={g}>Team: {GROUP_LABEL[g]}{n != null ? ` (${n})` : ""}</option>;
                })}
              </select>
            )}
            <label className="relative block">
              <FiSearch aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93A3]" />
              <input type="search" value={adminQ} onChange={(e) => { setAdminQ(e.target.value); setShowAllHits(false); }}
                placeholder={adminTab === "clients" ? "Search client or project (or type FPS)" : adminTab === "employees" ? "Search employee, client, project, FPS" : "Search person, project, task"}
                aria-label="Search" className="h-10 w-[min(280px,70vw)] pl-9 pr-3 rounded-[10px] border border-[#D5DAE3] bg-white text-[13px] box-border focus:outline-none focus:border-[#1D5FD1]" />
            </label>
          </div>
          {adminTab !== "sync" && (
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            <div role="tablist" aria-label="Period" className="flex bg-white border border-[#D5DAE3] rounded-[10px] p-1">
              {[{ id: "month", label: "Month" }, { id: "year", label: "Year" }].map((t) => (
                <button key={t.id} type="button" role="tab" aria-selected={adminPeriod === t.id} onClick={() => setParam("period", t.id, "month")}
                  className={`min-h-[32px] px-3 rounded-lg border-0 text-[13px] cursor-pointer ${adminPeriod === t.id ? "bg-[#E3ECFB] text-[#164AA6] font-semibold" : "bg-transparent text-[#3D4555] hover:bg-[#F4F6FA]"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center bg-white border border-[#D5DAE3] rounded-[10px] h-10">
              <button type="button" aria-label={`Previous ${adminPeriod}`} onClick={() => shiftAdmin(-1)}
                className="w-9 h-full flex items-center justify-center border-0 bg-transparent cursor-pointer text-[#3D4555] hover:text-[#1D5FD1]"><FiChevronLeft /></button>
              {/* Click the label: month / year calendar (click the year in its header to jump years) */}
              <DatePicker
                picker={adminPeriod}
                value={dayjs(`${selMonth}-01`)}
                format={adminPeriod === "year" ? "YYYY" : "MMMM YYYY"}
                allowClear={false}
                inputReadOnly
                variant="borderless"
                suffixIcon={null}
                aria-label={adminPeriod === "year" ? "Year to show" : "Month to show"}
                style={{ width: adminPeriod === "year" ? 70 : 140, padding: 0 }}
                className="[&_input]:!text-center [&_input]:!text-[13px] [&_input]:!font-medium [&_input]:!cursor-pointer"
                disabledDate={(d) => d && d.isAfter(dayjs(), adminPeriod)}
                onChange={(d) => d && setMonth(adminPeriod === "year"
                  ? (d.year() === today.getFullYear() ? currentYm : `${d.year()}-12`)
                  : d.format("YYYY-MM"))}
              />
              <button type="button" aria-label={`Next ${adminPeriod}`} onClick={() => shiftAdmin(1)} disabled={adminIsCurrent}
                className="w-9 h-full flex items-center justify-center border-0 bg-transparent cursor-pointer text-[#3D4555] hover:text-[#1D5FD1] disabled:opacity-30 disabled:cursor-default"><FiChevronRight /></button>
            </div>
            {!adminIsCurrent && (
              <button type="button" onClick={() => setMonth(currentYm)}
                className="h-10 px-3 rounded-[10px] border border-[#D5DAE3] bg-white text-[13px] text-[#1D5FD1] cursor-pointer whitespace-nowrap hover:bg-[#F8FAFC]">
                {adminPeriod === "year" ? "This year" : "This month"}
              </button>
            )}
            {exportButton("h-10")}
          </div>
          )}
        </div>

        {adminTab === "sync" && (
          <Card as="section" aria-label="Rocketlane sync" className="p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="m-0 text-[18px] font-semibold">Rocketlane sync</h2>
              <span className="text-[13px] text-[#5B6474]">
                What the daily Rocketlane job did on each run · open a run to see every entry, project and task it changed
              </span>
            </div>
            <RocketlaneSyncLog query={adminQ} />
          </Card>
        )}

        {adminTab === "employees" && (
          <Card as="section" aria-label="Employees" className="p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="m-0 text-[18px] font-semibold">Employees</h2>
              <span className="text-[13px] text-[#5B6474]">
                Hours from Activity Card and Rocketlane · utilization = hours ÷ capacity (working days while employed × 8 h)
              </span>
            </div>
            <EmployeesPanel key={adminKey} data={employeeData} state={employeeState} periodLabel={adminPeriodLabel} query={adminQ} />
          </Card>
        )}

        {/* Project search results (Clients tab) → Utilization page, that project */}
        {adminTab === "clients" && projSearchQ && (projState !== "ready" || projectHits.length > 0) && (
          <Card as="section" aria-label="Projects found" className="p-5 flex flex-col gap-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="m-0 text-[16px] font-semibold">
                Projects matching "{adminQ.trim()}"
                <span className="ml-2 text-[13px] font-normal text-[#5B6474]">{projState === "ready" ? `${projectHits.length} found · hours in ${adminPeriodLabel}` : "Loading…"}</span>
              </h2>
              <button type="button" onClick={() => setAdminQ("")}
                className="h-8 px-3 rounded-lg border border-[#D5DAE3] bg-white text-[12px] cursor-pointer hover:bg-[#F8FAFC]">Clear</button>
            </div>
            {projState === "ready" && projectHits.length === 0 && (
              <p className="m-0 text-[13px] text-[#5B6474]">No active project matches. Projects of inactive clients or closed projects aren't listed.</p>
            )}
            {projectHits.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse text-[13px]">
                  <thead>
                    <tr className="text-left text-[12px] text-[#5B6474] border-b border-[#EEF0F4]">
                      <th className="font-medium py-2 pl-1 pr-2">Project</th>
                      <th className="font-medium py-2 px-2">Client</th>
                      <th className="font-medium py-2 px-2 text-right">Hours</th>
                      <th className="font-medium py-2 px-2 text-right">Billable</th>
                      <th className="font-medium py-2 px-2 pr-1 text-right">People</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllHits ? projectHits : projectHits.slice(0, HITS_PREVIEW)).map((p) => (
                      <tr key={p.project_id} onClick={() => openProject(p.client_id, p.project_id)}
                        className="border-b border-[#F1F3F7] cursor-pointer hover:bg-[#F8FAFC]">
                        <td className="py-2.5 pl-1 pr-2">
                          <span className="flex items-center gap-1.5 min-w-0">
                            <span className="font-medium text-[#164AA6] truncate">{p.project_name}</span>
                            {(p.fps ?? isFps(p.project_name)) && <FpsChip />}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-[#3D4555]">{p.client_name}</td>
                        <td className="py-2.5 px-2 text-right font-semibold tabular-nums">{fmt(p.hours)} h</td>
                        <td className="py-2.5 px-2 text-right tabular-nums text-[#3D4555]">{p.hours ? `${fmt((p.billable_hours / p.hours) * 100)}%` : "—"}</td>
                        <td className="py-2.5 px-2 pr-1 text-right tabular-nums">{p.people_logged}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {projectHits.length > HITS_PREVIEW && (
              <button type="button" onClick={() => setShowAllHits((x) => !x)}
                className="self-start h-9 px-3 rounded-[10px] border-0 bg-transparent text-[13px] font-semibold text-[#1D5FD1] cursor-pointer hover:bg-[#EEF3FC]">
                {showAllHits ? "Show fewer" : `Show all ${projectHits.length} projects`}
              </button>
            )}
          </Card>
        )}

        {/* Clients → Utilization page */}
        {adminTab === "clients" && projState === "error" && (
          <div role="alert" className="rounded-[12px] border border-[#F6C9A8] bg-[#FFF4EC] px-4 py-3 text-[13px] text-[#7C2D12]">
            Couldn't load clients. Try refreshing.
          </div>
        )}
        {adminTab === "clients" && projState !== "error" && !(projState === "ready" && projects.length === 0) && (
          <Card as="section" aria-label="Clients" className="p-6 flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col gap-1">
                <h2 className="m-0 text-[18px] font-semibold">Clients</h2>
                <span className="text-[13px] text-[#5B6474]">
                  Hours logged in {adminPeriodLabel} · select a client to see its projects and each person's utilization
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {fpsSum.n > 0 && (
                  <span className="flex items-center gap-3 text-[12px] text-[#3D4555]">
                    <span className="flex items-center gap-1.5"><span aria-hidden="true" className="w-2.5 h-2.5 rounded-[3px]" style={{ background: C.primary }} />Normal {fmt(fpsSum.total - fpsSum.h)} h</span>
                    <span className="flex items-center gap-1.5"><span aria-hidden="true" className="w-2.5 h-2.5 rounded-[3px]" style={{ background: FPS_COLOR }} />FPS {fmt(fpsSum.h)} h</span>
                  </span>
                )}
                <button type="button" onClick={() => navigate(utilUrl(""))}
                  className="h-10 px-4 rounded-[10px] border border-[#D5DAE3] bg-white text-[13px] font-medium cursor-pointer hover:bg-[#F8FAFC]">
                  Open utilization
                </button>
              </div>
            </div>

            {projState === "loading" ? (
              <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))]">
                {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-[104px] rounded-[12px] bg-[#F1F3F7] animate-pulse" />)}
              </div>
            ) : projFiltered.length === 0 ? (
              <p className="m-0 text-[13px] text-[#5B6474]">No client or project matches "{adminQ}".</p>
            ) : (
              <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))]">
                {/* FPS projects of every client: who worked and how many hours */}
                {fpsSum.n > 0 && (!projQuery || projQuery === "fps") && (
                  <button type="button" onClick={openFps}
                    className="group text-left bg-[#FAF8FE] border border-[#E2D8F7] rounded-[12px] p-4 flex flex-col gap-2 cursor-pointer hover:border-[#C9B8F0] hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#7C3AED]">
                    <span className="flex items-start justify-between gap-2">
                      <span className="flex flex-col min-w-0">
                        <span className="text-[14px] font-semibold flex items-center gap-1.5 text-[#5B33A6]"><FiLayers aria-hidden="true" />FPS projects</span>
                        <span className="text-[12px] text-[#5B6474]">{fpsSum.n} project{fpsSum.n === 1 ? "" : "s"} in {fpsSum.clients} client{fpsSum.clients === 1 ? "" : "s"} · {fpsSum.withH} with hours</span>
                      </span>
                      <FiChevronRight aria-hidden="true" className="shrink-0 mt-1 text-[#8A93A3] group-hover:text-[#7C3AED]" />
                    </span>
                    <span className="text-[20px] font-semibold">{fmt(fpsSum.h)}<span className="text-[13px] text-[#5B6474] font-medium"> h</span></span>
                    <span className="h-1.5 bg-[#EDE6FB] rounded-full overflow-hidden">
                      <span className="block h-1.5 rounded-full" style={{ width: `${fpsSum.total ? (fpsSum.h / fpsSum.total) * 100 : 0}%`, background: FPS_COLOR }} />
                    </span>
                    <span className="text-[11px] text-[#5B6474]">
                      {fpsSum.h ? `${fmt((fpsSum.b / fpsSum.h) * 100)}% billable · ${fmt(fpsSum.total ? (fpsSum.h / fpsSum.total) * 100 : 0)}% of all hours` : "No FPS hours in this period"} · open to see who worked
                    </span>
                  </button>
                )}
                {projShown.map((c) => {
                  const billPct = c.total_hours ? (c.billable_hours / c.total_hours) * 100 : 0;
                  const fpsH = Number(c.fps_hours) || 0, normalH = Math.max(0, (Number(c.total_hours) || 0) - fpsH);
                  return (
                    <button
                      key={c.client_id} type="button" onClick={() => openClient(c.client_id)}
                      className="group text-left bg-white border border-[#E6E9EF] rounded-[12px] p-4 flex flex-col gap-2 cursor-pointer hover:border-[#BFD3F6] hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1D5FD1]"
                    >
                      <span className="flex items-start justify-between gap-2">
                        <span className="flex flex-col min-w-0">
                          <span className="text-[14px] font-semibold truncate group-hover:text-[#164AA6]">{c.client_name}</span>
                          <span className="text-[12px] text-[#5B6474]">
                            {c.projects_count} active project{c.projects_count === 1 ? "" : "s"}
                          </span>
                        </span>
                        <FiChevronRight aria-hidden="true" className="shrink-0 mt-1 text-[#8A93A3] group-hover:text-[#1D5FD1]" />
                      </span>
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="text-[20px] font-semibold">{fmt(c.total_hours)}<span className="text-[13px] text-[#5B6474] font-medium"> h</span></span>
                        <span className="text-[12px] text-[#5B6474] flex items-center gap-1" title="People who logged hours in this period">
                          <FiUsers aria-hidden="true" />{c.people_logged}
                        </span>
                      </span>
                      {/* Bar: normal (blue) + FPS (purple) */}
                      <span className="h-1.5 bg-[#F1F3F7] rounded-full overflow-hidden flex">
                        <span className="block h-1.5" style={{ width: `${(normalH / maxProjectHours) * 100}%`, background: C.primary }} />
                        {fpsH > 0 && <span className="block h-1.5" style={{ width: `${(fpsH / maxProjectHours) * 100}%`, background: FPS_COLOR }} />}
                      </span>
                      {/* Billable / non-billable: click → that client's billable (or non-billable) projects, tasks and people */}
                      <span className="text-[11px] text-[#5B6474] flex flex-wrap gap-x-2">
                        {c.total_hours ? (
                          <>
                            <span role="link" tabIndex={0} title="Show billable projects and tasks"
                              onClick={(ev) => { ev.stopPropagation(); openClient(c.client_id, "billable"); }}
                              onKeyDown={(ev) => { if (ev.key === "Enter") { ev.stopPropagation(); ev.preventDefault(); openClient(c.client_id, "billable"); } }}
                              className="text-[#164AA6] hover:underline cursor-pointer">{fmt(billPct)}% billable</span>
                            {c.non_billable_hours > 0 && (
                              <span role="link" tabIndex={0} title="Show non-billable projects and tasks"
                                onClick={(ev) => { ev.stopPropagation(); openClient(c.client_id, "nonbillable"); }}
                                onKeyDown={(ev) => { if (ev.key === "Enter") { ev.stopPropagation(); ev.preventDefault(); openClient(c.client_id, "nonbillable"); } }}
                                className="text-[#1E6B5F] hover:underline cursor-pointer">{fmt(c.non_billable_hours)} h non-billable</span>
                            )}
                          </>
                        ) : "No hours in this period"}
                      </span>
                      {c.fps_projects_count > 0 && (
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[#3D4555] border-t border-dashed border-[#E6E9EF] pt-1.5">
                          <span>Normal <b>{fmt(normalH)} h</b></span>
                          <span className="text-[#5B33A6]">FPS <b>{fmt(fpsH)} h</b> · {c.fps_projects_count} project{c.fps_projects_count === 1 ? "" : "s"}</span>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {!projQuery && projFiltered.length > PROJECTS_PREVIEW && (
              <button type="button" onClick={() => setShowAllProjects((x) => !x)}
                className="self-start h-9 px-3 rounded-[10px] border-0 bg-transparent text-[13px] font-semibold text-[#1D5FD1] cursor-pointer hover:bg-[#EEF3FC]">
                {showAllProjects ? "Show fewer" : `Show all ${projFiltered.length} clients`}
              </button>
            )}
          </Card>
        )}

        {/* Organization overview (secondary) */}
        <section aria-label="Organization overview" className="flex flex-col gap-3">
          <h2 className="m-0 text-[14px] font-semibold text-[#5B6474] uppercase tracking-[0.06em]">Organization overview</h2>
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
            <OrgTile label="Clients" value={org?.clients ?? "…"} />
            <OrgTile label="Projects" value={org?.projects ?? "…"}
              sub={org ? `Ops ${org.opsProjects ?? 0} · One-time ${org.onetimeProjects ?? 0} · Annual ${org.annualProjects ?? 0}` : null} />
            <OrgTile label="Active / inactive projects"
              value={<>{org?.activeProjects ?? "…"} <span className="text-[14px] text-[#5B6474] font-medium">/ {org?.inactiveProjects ?? "…"}</span></>} />
            <OrgTile label="Active employees" value={org?.activeEmployees ?? "…"} />
          </div>
        </section>
        </>)}

      </div>
    </div>
  );
};